import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import api from '../../services/api';

const STEPS = [
  { id: 'intro',    label: 'Inicio',      icon: '👋' },
  { id: 'document', label: 'Documento',   icon: '🪪' },
  { id: 'liveness', label: 'Liveness',    icon: '🤳' },
  { id: 'result',   label: 'Resultado',   icon: '✅' },
];

const LIVENESS_CHALLENGES = [
  { id: 'look_front', instruction: 'Mira directamente a la cámara', icon: '👁️', duration: 2000 },
  { id: 'blink',      instruction: 'Parpadea dos veces',            icon: '😉', duration: 3000 },
  { id: 'turn_right', instruction: 'Gira la cabeza a la derecha',   icon: '→',  duration: 2500 },
  { id: 'turn_left',  instruction: 'Gira la cabeza a la izquierda', icon: '←',  duration: 2500 },
  { id: 'look_front', instruction: 'Vuelve a mirar al frente',      icon: '✓',  duration: 1500 },
];

export default function KYC() {
  const { fetchMe } = useAuthStore();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const faceMeshRef = useRef(null);
  const animFrameRef = useRef(null);

  const [step, setStep] = useState('intro');
  const [docImage, setDocImage] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [livenessFrames, setLivenessFrames] = useState([]);

  // Camera state
  const [cameraMode, setCameraMode] = useState(null); // 'document' | 'liveness'
  const [cameraReady, setCameraReady] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceBox, setFaceBox] = useState(null);

  // Liveness state
  const [challengeIdx, setChallengeIdx] = useState(0);
  const [challengeProgress, setChallengeProgress] = useState(0);
  const [challengeComplete, setChallengeComplete] = useState(false);
  const [livenessRunning, setLivenessRunning] = useState(false);
  const livenessFramesRef = useRef([]);
  const challengeIdxRef = useRef(0);
  const challengeTimerRef = useRef(null);

  // Result state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Check existing KYC status
  useEffect(() => {
    api.get('/api/v1/kyc/status').then(r => {
      if (r.data?.kyc_status === 'APPROVED') setStep('result');
    }).catch(() => {});
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      if (challengeTimerRef.current) clearTimeout(challengeTimerRef.current);
    };
  }, []);

  // ── CAMERA ────────────────────────────────────────────────────────

  const startCamera = async (mode) => {
    try {
      setCameraMode(mode);
      setCameraReady(false);
      setFaceDetected(false);
      setError('');

      const constraints = {
        video: {
          facingMode: mode === 'liveness' ? 'user' : { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
          if (mode === 'liveness') {
            loadFaceMesh();
          }
        };
      }
    } catch (e) {
      setError('No se pudo acceder a la cámara. Permite el acceso en tu navegador.');
      setCameraMode(null);
    }
  };

  const stopCamera = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraMode(null);
    setCameraReady(false);
    setFaceDetected(false);
  };

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (cameraMode === 'liveness') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    if (cameraMode === 'liveness') {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    return canvas.toDataURL('image/jpeg', 0.85);
  }, [cameraMode]);

  // ── FACE MESH ─────────────────────────────────────────────────────

  const loadFaceMesh = async () => {
    try {
      // Load MediaPipe via CDN
      if (!window.FaceMesh) {
        await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js');
      }
      if (!window.Camera) {
        await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js');
      }

      const faceMesh = new window.FaceMesh({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      faceMesh.onResults((results) => {
        handleFaceMeshResults(results);
      });

      faceMeshRef.current = faceMesh;

      // Start processing frames
      processVideoFrame();

    } catch (e) {
      console.warn('MediaPipe not available, using fallback:', e);
      // Fallback — simple timer-based liveness
      startFallbackLiveness();
    }
  };

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
      const script = document.createElement('script');
      script.src = src;
      script.crossOrigin = 'anonymous';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const processVideoFrame = async () => {
    if (!videoRef.current || !faceMeshRef.current) return;
    if (videoRef.current.readyState >= 2) {
      await faceMeshRef.current.send({ image: videoRef.current });
    }
    animFrameRef.current = requestAnimationFrame(processVideoFrame);
  };

  // Liveness state tracking
  const blinkCountRef = useRef(0);
  const prevEARRef = useRef(1);
  const headPoseRef = useRef({ yaw: 0 });

  const handleFaceMeshResults = useCallback((results) => {
    if (!results.multiFaceLandmarks || results.multiFaceLandmarks.length === 0) {
      setFaceDetected(false);
      setFaceBox(null);
      return;
    }

    setFaceDetected(true);
    const landmarks = results.multiFaceLandmarks[0];

    // Draw face overlay on canvas
    drawFaceOverlay(landmarks);

    // Capture frames for liveness
    if (livenessRunning) {
      const frame = captureFrame();
      if (frame) {
        livenessFramesRef.current.push(frame);
        if (livenessFramesRef.current.length > 30) livenessFramesRef.current.shift();
      }
    }

    // Eye Aspect Ratio for blink detection
    const leftEye = [33, 160, 158, 133, 153, 144];
    const rightEye = [362, 385, 387, 263, 373, 380];
    const ear = calculateEAR(landmarks, leftEye, rightEye);

    if (ear < 0.2 && prevEARRef.current >= 0.2) {
      blinkCountRef.current += 1;
    }
    prevEARRef.current = ear;

    // Head pose estimation (yaw from nose tip vs face center)
    const nose = landmarks[1];
    const leftCheek = landmarks[234];
    const rightCheek = landmarks[454];
    const faceWidth = Math.abs(rightCheek.x - leftCheek.x);
    const noseOffset = (nose.x - (leftCheek.x + rightCheek.x) / 2) / faceWidth;
    headPoseRef.current = { yaw: noseOffset };

    // Update challenge progress
    updateChallengeProgress(blinkCountRef.current, noseOffset);

  }, [livenessRunning, captureFrame]);

  const calculateEAR = (landmarks, leftEye, rightEye) => {
    try {
      const lm = landmarks;
      const leftEAR = (
        dist(lm[leftEye[1]], lm[leftEye[5]]) +
        dist(lm[leftEye[2]], lm[leftEye[4]])
      ) / (2 * dist(lm[leftEye[0]], lm[leftEye[3]]));

      const rightEAR = (
        dist(lm[rightEye[1]], lm[rightEye[5]]) +
        dist(lm[rightEye[2]], lm[rightEye[4]])
      ) / (2 * dist(lm[rightEye[0]], lm[rightEye[3]]));

      return (leftEAR + rightEAR) / 2;
    } catch { return 0.3; }
  };

  const dist = (a, b) => Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));

  const drawFaceOverlay = (landmarks) => {
    if (!canvasRef.current || !videoRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Mirror for selfie
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);

    // Draw face outline dots
    const faceContour = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109];

    ctx.beginPath();
    faceContour.forEach((idx, i) => {
      const pt = landmarks[idx];
      const x = pt.x * canvas.width;
      const y = pt.y * canvas.height;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.strokeStyle = 'rgba(201, 74, 30, 0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Eye dots
    [33, 133, 362, 263].forEach(idx => {
      const pt = landmarks[idx];
      ctx.beginPath();
      ctx.arc(pt.x * canvas.width, pt.y * canvas.height, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#c94a1e';
      ctx.fill();
    });

    ctx.restore();
  };

  // Challenge progress logic
  const challengePassedRef = useRef([false, false, false, false, false]);

  const updateChallengeProgress = useCallback((blinkCount, yaw) => {
    const idx = challengeIdxRef.current;
    const challenge = LIVENESS_CHALLENGES[idx];
    if (!challenge || challengePassedRef.current[idx]) return;

    let passed = false;
    if (challenge.id === 'look_front') {
      passed = Math.abs(yaw) < 0.1;
    } else if (challenge.id === 'blink') {
      passed = blinkCount >= 2;
    } else if (challenge.id === 'turn_right') {
      passed = yaw > 0.2;
    } else if (challenge.id === 'turn_left') {
      passed = yaw < -0.2;
    }

    if (passed) {
      challengePassedRef.current[idx] = true;
      // Auto-advance after short delay
      challengeTimerRef.current = setTimeout(() => {
        const next = idx + 1;
        if (next >= LIVENESS_CHALLENGES.length) {
          // All challenges complete — capture final selfie
          const finalFrame = captureFrame();
          setSelfieImage(finalFrame);
          setLivenessFrames([...livenessFramesRef.current]);
          setChallengeComplete(true);
          setLivenessRunning(false);
          stopCamera();
        } else {
          challengeIdxRef.current = next;
          setChallengeIdx(next);
          setChallengeProgress(0);
        }
      }, 600);
    }

    setChallengeProgress(passed ? 100 : Math.min(90, Math.abs(yaw) * 200));
  }, [captureFrame]);

  // Fallback liveness (no MediaPipe) — timer based
  const startFallbackLiveness = () => {
    setLivenessRunning(true);
    let idx = 0;
    challengeIdxRef.current = 0;
    setChallengeIdx(0);

    const advance = () => {
      // Capture frame
      const frame = captureFrame();
      if (frame) livenessFramesRef.current.push(frame);

      idx++;
      if (idx >= LIVENESS_CHALLENGES.length) {
        const finalFrame = captureFrame();
        setSelfieImage(finalFrame);
        setLivenessFrames([...livenessFramesRef.current]);
        setChallengeComplete(true);
        setLivenessRunning(false);
        stopCamera();
      } else {
        challengeIdxRef.current = idx;
        setChallengeIdx(idx);
        const nextChallenge = LIVENESS_CHALLENGES[idx];
        challengeTimerRef.current = setTimeout(advance, nextChallenge.duration);
      }
    };

    const firstChallenge = LIVENESS_CHALLENGES[0];
    challengeTimerRef.current = setTimeout(advance, firstChallenge.duration);
  };

  const startLiveness = () => {
    setLivenessRunning(true);
    challengeIdxRef.current = 0;
    challengePassedRef.current = [false, false, false, false, false];
    blinkCountRef.current = 0;
    livenessFramesRef.current = [];
    setChallengeIdx(0);
    setChallengeProgress(0);
    setChallengeComplete(false);
  };

  // ── DOCUMENT CAPTURE ──────────────────────────────────────────────

  const captureDocument = () => {
    const frame = captureFrame();
    if (frame) {
      setDocImage(frame);
      stopCamera();
    }
  };

  const handleDocUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setDocImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── SUBMIT ────────────────────────────────────────────────────────

  const submitVerification = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/v1/kyc/verify', {
        document_image: docImage,
        selfie_image: selfieImage,
        liveness_frames: livenessFrames.length > 0 ? livenessFrames.slice(0, 10) : null,
      });
      setResult(res.data);
      setStep('result');
      await fetchMe();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar la verificación');
      setStep('result');
      setResult({ kyc_status: 'REVIEW', message: 'Tu verificación está en revisión manual.' });
    } finally {
      setLoading(false);
    }
  };

  const currentStepIdx = STEPS.findIndex(s => s.id === step);
  const currentChallenge = LIVENESS_CHALLENGES[challengeIdx];

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '16px 0' }}>
      <style>{`
        .kyc-video {
          width: 100%; border-radius: 12px; display: block;
          transform: scaleX(-1);
        }
        .kyc-canvas {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%;
          border-radius: 12px; pointer-events: none;
        }
        .kyc-video-doc { transform: none !important; }
        .challenge-bar {
          height: 6px; background: #e2dbd0; border-radius: 3px; overflow: hidden; margin-top: 8px;
        }
        .challenge-bar-fill {
          height: 100%; background: #c94a1e; border-radius: 3px;
          transition: width .3s ease;
        }
        @media (max-width: 768px) {
          .kyc-card { padding: 20px 16px !important; }
        }
      `}</style>

      {/* Progress steps */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28, padding: '0 4px' }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', fontSize: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: currentStepIdx > i ? '#2d6a4f' : step === s.id ? '#c94a1e' : '#e2dbd0',
                color: currentStepIdx >= i ? '#fff' : '#7a7060',
              }}>
                {currentStepIdx > i ? '✓' : s.icon}
              </div>
              <span style={{ fontSize: 10, color: '#7a7060', fontWeight: 600 }}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: currentStepIdx > i ? '#2d6a4f' : '#e2dbd0', margin: '0 6px', marginBottom: 20 }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={{ background: '#faeef4', border: '1px solid #f4b8c8', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#9b2c5e' }}>
          ⚠ {error}
        </div>
      )}

      {/* ── INTRO ── */}
      {step === 'intro' && (
        <div className="kyc-card" style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🪪</div>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 24, margin: '0 0 12px' }}>Verificación de identidad</h2>
          <p style={{ fontSize: 14, color: '#7a7060', lineHeight: 1.7, marginBottom: 24 }}>
            Proceso automático en menos de 2 minutos. Necesitas tu documento de identidad y la cámara del dispositivo.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
            {[
              { icon: '🪪', text: 'Foto del documento' },
              { icon: '👁️', text: 'Liveness check' },
              { icon: '⚡', text: 'Resultado automático' },
            ].map((item, i) => (
              <div key={i} style={{ background: '#f9f6f1', borderRadius: 10, padding: '14px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>{item.icon}</div>
                <div style={{ fontSize: 11, color: '#7a7060', lineHeight: 1.4 }}>{item.text}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#eaf1fb', borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 12, color: '#1a4e8c', textAlign: 'left' }}>
            🔒 Tus imágenes se procesan en nuestros servidores seguros y no se comparten con terceros.
          </div>
          <button onClick={() => setStep('document')} style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            Comenzar verificación →
          </button>
        </div>
      )}

      {/* ── DOCUMENT ── */}
      {step === 'document' && (
        <div className="kyc-card" style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 28 }}>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 20, margin: '0 0 6px' }}>Foto del documento</h2>
          <p style={{ fontSize: 13, color: '#7a7060', marginBottom: 20 }}>
            Pasaporte, DNI, carnet de identidad o licencia vigente. Colócalo sobre superficie plana con buena luz.
          </p>

          {!docImage ? (
            cameraMode === 'document' ? (
              <div>
                <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 12, background: '#000' }}>
                  <video ref={videoRef} className="kyc-video kyc-video-doc" autoPlay muted playsInline />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  {!cameraReady && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0d0b', color: '#fff', fontSize: 13 }}>
                      Iniciando cámara...
                    </div>
                  )}
                  {/* Document guide overlay */}
                  {cameraReady && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={{ width: '85%', height: '55%', border: '2px dashed rgba(201,74,30,0.8)', borderRadius: 8 }} />
                    </div>
                  )}
                </div>
                <p style={{ fontSize: 12, color: '#7a7060', textAlign: 'center', marginBottom: 12 }}>
                  Encuadra el documento dentro del rectángulo
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={captureDocument} disabled={!cameraReady} style={{ flex: 1, padding: '13px', borderRadius: 8, border: 'none', background: cameraReady ? '#c94a1e' : '#e2dbd0', color: '#fff', fontSize: 14, fontWeight: 600, cursor: cameraReady ? 'pointer' : 'not-allowed' }}>
                    📷 Capturar documento
                  </button>
                  <button onClick={stopCamera} style={{ padding: '13px 16px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <button onClick={() => startCamera('document')} style={{ padding: '20px 12px', borderRadius: 12, border: '2px dashed #c94a1e', background: '#fdf4f0', cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#c94a1e' }}>Usar cámara</div>
                    <div style={{ fontSize: 11, color: '#7a7060', marginTop: 2 }}>Cámara trasera</div>
                  </button>
                  <label style={{ padding: '20px 12px', borderRadius: 12, border: '2px dashed #e2dbd0', background: '#f9f6f1', cursor: 'pointer', textAlign: 'center', display: 'block' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#7a7060' }}>Subir foto</div>
                    <div style={{ fontSize: 11, color: '#7a7060', marginTop: 2 }}>JPG, PNG</div>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleDocUpload} />
                  </label>
                </div>
                <div style={{ background: '#fef3e2', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#b45309' }}>
                  💡 Asegúrate de que el documento esté completamente visible, sin reflejos ni sombras.
                </div>
              </div>
            )
          ) : (
            <div>
              <img src={docImage} alt="Documento" style={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 10, border: '1px solid #e2dbd0', marginBottom: 16 }} />
              <div style={{ background: '#eaf4ef', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#2d6a4f', marginBottom: 16 }}>
                ✓ Documento capturado correctamente
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setDocImage(null)} style={{ flex: 1, padding: '12px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
                  ↩ Repetir
                </button>
                <button onClick={() => { setStep('liveness'); }} style={{ flex: 2, padding: '12px', borderRadius: 8, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Continuar →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── LIVENESS ── */}
      {step === 'liveness' && (
        <div className="kyc-card" style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 16, padding: 28 }}>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 20, margin: '0 0 6px' }}>Verificación de liveness</h2>
          <p style={{ fontSize: 13, color: '#7a7060', marginBottom: 20 }}>
            Sigue las instrucciones para confirmar que eres una persona real. El proceso es automático.
          </p>

          {challengeComplete ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
              <h3 style={{ fontFamily: 'Georgia,serif', fontSize: 20, marginBottom: 8 }}>¡Liveness confirmado!</h3>
              {selfieImage && (
                <img src={selfieImage} alt="Selfie" style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: '50%', border: '3px solid #c94a1e', marginBottom: 16 }} />
              )}
              <button onClick={submitVerification} disabled={loading} style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: loading ? '#e2dbd0' : '#c94a1e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Verificando...' : 'Verificar identidad →'}
              </button>
            </div>
          ) : cameraMode === 'liveness' ? (
            <div>
              {/* Video + canvas overlay */}
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 16, background: '#000', aspectRatio: '4/3' }}>
                <video ref={videoRef} className="kyc-video" autoPlay muted playsInline style={{ width: '100%', display: 'block', transform: 'scaleX(-1)' }} />
                <canvas ref={canvasRef} className="kyc-canvas" />

                {/* Face detection indicator */}
                <div style={{ position: 'absolute', top: 12, left: 12, padding: '4px 10px', borderRadius: 20, background: faceDetected ? 'rgba(45,106,79,.85)' : 'rgba(155,44,94,.85)', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                  {faceDetected ? '✓ Cara detectada' : '○ Buscando cara...'}
                </div>

                {/* Challenge overlay */}
                {livenessRunning && currentChallenge && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,.7))', padding: '20px 16px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 28, marginBottom: 4 }}>{currentChallenge.icon}</div>
                    <div style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>{currentChallenge.instruction}</div>
                    <div className="challenge-bar" style={{ background: 'rgba(255,255,255,.2)', margin: '8px 20px 0' }}>
                      <div className="challenge-bar-fill" style={{ width: `${challengeProgress}%`, background: '#c94a1e' }} />
                    </div>
                    <div style={{ color: 'rgba(255,255,255,.6)', fontSize: 11, marginTop: 4 }}>
                      Paso {challengeIdx + 1} de {LIVENESS_CHALLENGES.length}
                    </div>
                  </div>
                )}
              </div>

              {!livenessRunning ? (
                <div>
                  <div style={{ background: '#eaf1fb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#1a4e8c', marginBottom: 12 }}>
                    💡 Asegúrate de tener buena iluminación frontal y mirar directamente a la cámara.
                  </div>
                  <button onClick={startLiveness} disabled={!cameraReady} style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: cameraReady ? '#c94a1e' : '#e2dbd0', color: '#fff', fontSize: 15, fontWeight: 700, cursor: cameraReady ? 'pointer' : 'not-allowed' }}>
                    {cameraReady ? '▶ Iniciar liveness check' : 'Iniciando cámara...'}
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', fontSize: 13, color: '#7a7060' }}>
                  Sigue las instrucciones en pantalla...
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🤳</div>
              <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 8 }}>
                Se abrirá tu cámara frontal. Sigue las instrucciones que aparecen en pantalla.
              </p>
              <div style={{ display: 'grid', gap: 8, marginBottom: 20 }}>
                {LIVENESS_CHALLENGES.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f9f6f1', borderRadius: 8 }}>
                    <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{c.icon}</span>
                    <span style={{ fontSize: 13, color: '#4a4035' }}>{c.instruction}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => startCamera('liveness')} style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                Abrir cámara frontal →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── RESULT ── */}
      {step === 'result' && (
        <div className="kyc-card" style={{ background: '#fff', border: `1px solid ${result?.kyc_status === 'APPROVED' ? '#2d6a4f' : result?.kyc_status === 'REJECTED' ? '#9b2c5e' : '#e2dbd0'}`, borderRadius: 16, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>
            {result?.kyc_status === 'APPROVED' ? '✅' : result?.kyc_status === 'REJECTED' ? '❌' : '⏳'}
          </div>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 24, margin: '0 0 12px', color: result?.kyc_status === 'APPROVED' ? '#2d6a4f' : result?.kyc_status === 'REJECTED' ? '#9b2c5e' : '#0f0d0b' }}>
            {result?.kyc_status === 'APPROVED' ? '¡Identidad verificada!' : result?.kyc_status === 'REJECTED' ? 'No aprobado' : 'En revisión manual'}
          </h2>
          <p style={{ fontSize: 14, color: '#7a7060', lineHeight: 1.7, marginBottom: 24 }}>
            {result?.message || (result?.kyc_status === 'REVIEW' ? 'Un agente revisará tu solicitud en menos de 24 horas.' : '')}
          </p>

          {result?.score > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
              {[
                { label: 'Face Match', value: `${(result.face_match || 0).toFixed(0)}%` },
                { label: 'Liveness', value: `${(result.liveness || 0).toFixed(0)}%` },
                { label: 'Autenticidad', value: `${(result.authenticity || 0).toFixed(0)}%` },
              ].map((s, i) => (
                <div key={i} style={{ background: '#f9f6f1', borderRadius: 10, padding: '12px 8px' }}>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, marginBottom: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#7a7060' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            {result?.kyc_status === 'REJECTED' && (
              <button onClick={() => { setStep('intro'); setDocImage(null); setSelfieImage(null); setLivenessFrames([]); setResult(null); setChallengeComplete(false); }} style={{ flex: 1, padding: '12px', borderRadius: 8, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Intentar de nuevo
              </button>
            )}
            <button onClick={() => navigate('/app/dashboard')} style={{ flex: 1, padding: '12px', borderRadius: 8, border: result?.kyc_status === 'REJECTED' ? '1px solid #e2dbd0' : 'none', background: result?.kyc_status === 'REJECTED' ? '#fff' : '#c94a1e', color: result?.kyc_status === 'REJECTED' ? '#7a7060' : '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {result?.kyc_status === 'APPROVED' ? 'Ir al dashboard →' : 'Volver al dashboard'}
            </button>
          </div>
        </div>
      )}

      {/* Hidden canvas for capture */}
      {step !== 'liveness' && <canvas ref={canvasRef} style={{ display: 'none' }} />}
    </div>
  );
}
